import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  FaSearch, FaSortAmountDown, FaSortAmountUp,
  FaFilter, FaEdit, FaTrash, FaDownload, FaTimes, FaCheck, FaTable
} from 'react-icons/fa';

const categoryEmoji = {
  Food: '🍔', Salary: '💼', Utilities: '⚡', Electronics: '💻',
  Health: '💪', Transport: '🚗', Housing: '🏠', Entertainment: '🎬',
  Healthcare: '🏥', Shopping: '🛍️', Investment: '📈', Other: '📁',
};

const CATEGORIES = ['Food', 'Salary', 'Utilities', 'Electronics', 'Health', 'Transport', 'Housing', 'Entertainment', 'Healthcare', 'Shopping', 'Investment', 'Other'];

const TransactionHistory = () => {
  const { transactions, updateTransaction, deleteTransaction, role, formatCurrency } = useApp();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({ type: 'all', category: 'all', startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  const categories = useMemo(() => [...new Set(transactions.map(t => t.category))].sort(), [transactions]);

  const filtered = useMemo(() => {
    let result = [...transactions];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(tx =>
        tx.title.toLowerCase().includes(term) ||
        tx.category.toLowerCase().includes(term) ||
        tx.amount.toString().includes(term)
      );
    }
    if (filters.type !== 'all') result = result.filter(tx => tx.type === filters.type);
    if (filters.category !== 'all') result = result.filter(tx => tx.category === filters.category);
    if (filters.startDate) result = result.filter(tx => tx.date >= filters.startDate);
    if (filters.endDate) result = result.filter(tx => tx.date <= filters.endDate);
    result.sort((a, b) => {
      let aVal = a[sortConfig.key], bVal = b[sortConfig.key];
      if (sortConfig.key === 'amount') { aVal = parseFloat(aVal); bVal = parseFloat(bVal); }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [transactions, searchTerm, filters, sortConfig]);

  const handleSort = (key) =>
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));

  const startEdit = (tx) => {
    setEditingId(tx.id);
    setEditForm({ title: tx.title, amount: tx.amount, category: tx.category, type: tx.type, date: tx.date });
  };

  const saveEdit = () => {
    if (!editForm.title || !editForm.amount) return;
    updateTransaction(editingId, editForm);
    setEditingId(null);
  };

  const exportCSV = () => {
    const rows = [['Date', 'Title', 'Category', 'Type', 'Amount'],
      ...filtered.map(tx => [tx.date, `"${tx.title}"`, tx.category, tx.type, tx.amount])];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: 'transactions.csv' }).click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: 'transactions.json' }).click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <span style={{ opacity: 0.3, marginLeft: '4px' }}>↕</span>;
    return sortConfig.direction === 'asc'
      ? <FaSortAmountUp size={11} style={{ marginLeft: '4px', color: 'var(--accent)' }} />
      : <FaSortAmountDown size={11} style={{ marginLeft: '4px', color: 'var(--accent)' }} />;
  };

  const activeFilterCount = Object.entries(filters).filter(([, v]) => v !== 'all' && v !== '').length;

  // Summary stats bar
  const totalInFiltered = filtered.reduce((s, tx) => tx.type === 'income' ? s + tx.amount : s - tx.amount, 0);

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">
            Showing <strong style={{ color: 'var(--accent)' }}>{filtered.length}</strong> of {transactions.length} records
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={exportCSV} className="btn btn-ghost btn-sm">
            <FaDownload size={11} /> CSV
          </button>
          <button onClick={exportJSON} className="btn btn-ghost btn-sm">
            <FaDownload size={11} /> JSON
          </button>
        </div>
      </div>

      {/* Stats mini-bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0.75rem',
      }}>
        {[
          {
            label: 'Filtered Income',
            value: formatCurrency(filtered.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0)),
            color: 'var(--success)', bg: 'var(--success-light)', border: 'rgba(52,211,153,0.2)',
          },
          {
            label: 'Filtered Expenses',
            value: formatCurrency(filtered.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0)),
            color: 'var(--danger)', bg: 'var(--danger-light)', border: 'rgba(248,113,113,0.2)',
          },
          {
            label: 'Net (Filtered)',
            value: (totalInFiltered >= 0 ? '+' : '') + formatCurrency(Math.abs(totalInFiltered)),
            color: totalInFiltered >= 0 ? 'var(--success)' : 'var(--danger)',
            bg: totalInFiltered >= 0 ? 'var(--success-light)' : 'var(--danger-light)',
            border: totalInFiltered >= 0 ? 'rgba(52,211,153,0.2)' : 'rgba(248,113,113,0.2)',
          },
        ].map((stat) => (
          <div key={stat.label} style={{
            padding: '0.85rem 1rem',
            borderRadius: '14px',
            background: stat.bg,
            backdropFilter: 'blur(8px)',
            border: `1px solid ${stat.border}`,
            display: 'flex', flexDirection: 'column', gap: '0.25rem',
          }}>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</p>
            <p style={{ fontSize: '1.05rem', fontWeight: 800, color: stat.color, letterSpacing: '-0.02em' }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Search + Filter Bar */}
      <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '220px' }}>
          <FaSearch size={12} style={{
            position: 'absolute', left: '1rem', top: '50%',
            transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none',
          }} />
          <input
            id="search-transactions"
            type="text"
            className="input-field"
            placeholder="Search by title, category, amount..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '2.5rem' }}
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn btn-ghost"
          style={{ position: 'relative', whiteSpace: 'nowrap', flex: 'none' }}
        >
          <FaFilter size={12} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{
              position: 'absolute', top: '-7px', right: '-7px',
              width: '19px', height: '19px', borderRadius: '50%',
              background: 'var(--gradient-accent)',
              color: 'white', fontSize: '0.68rem', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(99,102,241,0.5)',
            }}>{activeFilterCount}</span>
          )}
        </button>

        {activeFilterCount > 0 && (
          <button onClick={() => setFilters({ type: 'all', category: 'all', startDate: '', endDate: '' })} className="btn btn-ghost btn-sm">
            <FaTimes size={10} /> Reset
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="card slide-in" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            <div>
              <label className="form-label">Type</label>
              <select className="input-field" value={filters.type} onChange={e => setFilters(p => ({ ...p, type: e.target.value }))}>
                <option value="all">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
            </div>
            <div>
              <label className="form-label">Category</label>
              <select className="input-field" value={filters.category} onChange={e => setFilters(p => ({ ...p, category: e.target.value }))}>
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">From Date</label>
              <input type="date" className="input-field" value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">To Date</label>
              <input type="date" className="input-field" value={filters.endDate} min={filters.startDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} />
            </div>
          </div>
        </div>
      )}

      {/* Table Card */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Table header bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--border)',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <FaTable size={13} color="var(--text-muted)" />
          <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {filtered.length} {filtered.length === 1 ? 'Record' : 'Records'}
          </span>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('title')} style={{ paddingLeft: '1.25rem' }}>
                  Description <SortIcon col="title" />
                </th>
                <th onClick={() => handleSort('date')}>Date <SortIcon col="date" /></th>
                <th onClick={() => handleSort('category')}>Category <SortIcon col="category" /></th>
                <th onClick={() => handleSort('type')}>Type <SortIcon col="type" /></th>
                <th onClick={() => handleSort('amount')} style={{ textAlign: 'right', paddingRight: '1.25rem' }}>
                  Amount <SortIcon col="amount" />
                </th>
                {role === 'admin' && <th style={{ textAlign: 'center' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={role === 'admin' ? 6 : 5} style={{ textAlign: 'center', padding: '4rem 1rem', color: 'var(--text-muted)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '60px', height: '60px', borderRadius: '18px',
                        background: 'var(--accent-light)', border: '1px solid var(--border-glow)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem',
                      }}>🔍</div>
                      <p style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-secondary)' }}>No transactions found</p>
                      <p style={{ fontSize: '0.82rem' }}>Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              ) : filtered.map((tx, idx) => (
                editingId === tx.id ? (
                  <tr key={tx.id} style={{ background: 'var(--accent-light)' }}>
                    <td style={{ paddingLeft: '1.25rem' }}>
                      <input className="input-field" style={{ padding: '0.4rem 0.65rem', fontSize: '0.82rem' }}
                        value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} />
                    </td>
                    <td>
                      <input type="date" className="input-field" style={{ padding: '0.4rem 0.65rem', fontSize: '0.82rem' }}
                        value={editForm.date} onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} />
                    </td>
                    <td>
                      <select className="input-field" style={{ padding: '0.4rem 0.65rem', fontSize: '0.82rem' }}
                        value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="input-field" style={{ padding: '0.4rem 0.65rem', fontSize: '0.82rem' }}
                        value={editForm.type} onChange={e => setEditForm(p => ({ ...p, type: e.target.value }))}>
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                      </select>
                    </td>
                    <td style={{ textAlign: 'right', paddingRight: '0.75rem' }}>
                      <input type="number" className="input-field" style={{ padding: '0.4rem 0.65rem', textAlign: 'right', fontSize: '0.82rem' }}
                        value={editForm.amount} onChange={e => setEditForm(p => ({ ...p, amount: e.target.value }))} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                        <button onClick={saveEdit} className="btn btn-success btn-sm"><FaCheck size={11} /></button>
                        <button onClick={() => setEditingId(null)} className="btn btn-ghost btn-sm"><FaTimes size={11} /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={tx.id} className="fade-in" style={{ animationDelay: `${idx * 0.02}s` }}>
                    <td style={{ paddingLeft: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '11px',
                          background: tx.type === 'income' ? 'var(--success-light)' : 'var(--danger-light)',
                          border: tx.type === 'income' ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(248,113,113,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1rem', flexShrink: 0,
                          boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                        }}>
                          {categoryEmoji[tx.category] || '📁'}
                        </div>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{tx.title}</span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem', whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {formatDate(tx.date)}
                    </td>
                    <td>
                      <span className="badge badge-category">{tx.category}</span>
                    </td>
                    <td>
                      <span className={`badge ${tx.type === 'income' ? 'badge-income' : 'badge-expense'}`} style={{ textTransform: 'capitalize' }}>
                        {tx.type}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', paddingRight: '1.25rem', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '-0.01em', color: tx.type === 'income' ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                      {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                    </td>
                    {role === 'admin' && (
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center' }}>
                          <button onClick={() => startEdit(tx)} className="btn btn-ghost btn-sm" title="Edit" style={{ padding: '0.35rem 0.6rem' }}>
                            <FaEdit size={12} color="var(--accent)" />
                          </button>
                          {confirmDelete === tx.id ? (
                            <>
                              <button onClick={() => { deleteTransaction(tx.id); setConfirmDelete(null); }} className="btn btn-danger btn-sm" style={{ padding: '0.35rem 0.6rem' }}>
                                <FaCheck size={11} />
                              </button>
                              <button onClick={() => setConfirmDelete(null)} className="btn btn-ghost btn-sm" style={{ padding: '0.35rem 0.6rem' }}>
                                <FaTimes size={11} />
                              </button>
                            </>
                          ) : (
                            <button onClick={() => setConfirmDelete(tx.id)} className="btn btn-ghost btn-sm" title="Delete" style={{ padding: '0.35rem 0.6rem' }}>
                              <FaTrash size={12} color="var(--danger)" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
